// hooks/useFileHandling.js
import { useState, useRef } from 'react';

/**
 * Custom hook for managing file attachments with parsing for CSV/JSON
 */
export const useFileHandling = ({ processFile }) => {
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [keepFiles, setKeepFiles] = useState(false);
  const [fileError, setFileError] = useState('');
  const [parsedFileData, setParsedFileData] = useState(null);
  const fileInputRef = useRef(null);

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

/// Helper to clean unwanted characters (unchanged)
const cleanString = (str) => {
  if (typeof str !== 'string') return str;
  return str
    .replace(/Â/g, '')              // Remove Â character
    .replace(/\r\n|\r/g, '\n')     // Normalize Windows/Unix newlines, keep \n
    //.replace(/\s+/g, ' ')           // Collapse multiple spaces into one (outside quotes)
    .trim();                        // Remove leading/trailing spaces
};

// Helper to parse a single CSV line, respecting quotes
const parseCSVLine = (line) => {
  const values = [];
  let currentValue = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes; // Toggle quote state
      currentValue += char; // Keep quotes for now
    } else if (char === ',' && !inQuotes) {
      values.push(stripQuotes(currentValue));
      currentValue = '';
    } else {
      currentValue += char;
    }
  }
  if (currentValue) {
    values.push(stripQuotes(currentValue)); // Add last value
  }
  return values;
};

// Helper to strip leading/trailing quotes from a value
const stripQuotes = (value) => {
  value = value.trim();
  if (value.startsWith('"') && value.endsWith('"')) {
    return value.slice(1, -1); // Remove surrounding quotes
  }
  return value;
};

// Parse CSV string to JSON
const parseCSV = (csvString) => {
  let cleanedString = cleanString(csvString);
  try {
    // Split into lines while respecting quoted fields
    const lines = [];
    let currentLine = '';
    let inQuotes = false;

    // Process character by character to handle multi-line quoted fields
    for (let i = 0; i < cleanedString.length; i++) {
      const char = cleanedString[i];
      if (char === '"') {
        inQuotes = !inQuotes; // Toggle quote state
        currentLine += char;
      } else if (char === '\n' && !inQuotes) {
        lines.push(currentLine);
        currentLine = '';
      } else {
        currentLine += char;
      }
    }
    if (currentLine) lines.push(currentLine); // Add last line

    // Filter out empty lines
    const filteredLines = lines.filter(line => line.trim().length > 0);
    if (filteredLines.length < 2) {
      return JSON.stringify(cleanedString); // Return as text if no data rows
    }

    // Define expected headers
    const expectedHeaders = 'Issue key,Issue id,Summary,Status,Created,Description';

    // Parse headers
    const headers = parseCSVLine(filteredLines[0]);
    const headerString = headers.join(',');

    // Check if headers match exactly
    if (headerString !== expectedHeaders) {
      return JSON.stringify(cleanedString); // Return cleaned text as JSON string if headers don't match
    }

    // Parse data rows into array of objects
    const result = [];
    for (let i = 1; i < filteredLines.length; i++) {
      const values = parseCSVLine(filteredLines[i]);
      if (values.length === headers.length) {
        const obj = {};
        headers.forEach((header, index) => {
          obj[header] = values[index];
        });
        result.push(obj);
      }
    }

    // Return JSON string if parsing succeeds
    return JSON.stringify(result.length > 0 ? result : cleanedString);
  } catch (error) {
    console.error('Error parsing CSV:', error);
    return JSON.stringify(cleanedString); // Fallback to cleaned text on error
  }
};

  // Parse file content based on file type
  const parseFileContent = (file) => {
    try {
      // Validate file object
      if (!file || !file.fileContent || !file.fileName) {
        throw new Error('Invalid file object: missing required properties');
      }
  
      const fileType = file.fileType.toLowerCase();
      const fileName = file.fileName.toLowerCase();
  
      // JSON parsing
      if (fileType === 'json' || fileName.endsWith('.json')) {
        return typeof file.fileContent === 'string'
          ? JSON.parse(file.fileContent)
          : file.fileContent; // Return as-is if already parsed
      }
  
      // CSV parsing (converted to JSON)
      if (fileType === 'csv' || fileName.endsWith('.csv')) {
        if (typeof file.fileContent !== 'string') {
          throw new Error('CSV content must be a string');
        }
        return parseCSV(file.fileContent);
      }
  
      // Plain text (.txt)
      if (fileType === 'text' || fileName.endsWith('.txt')) {
        if (typeof file.fileContent === 'string') {
          return file.fileContent.trim(); // Return trimmed text
        }
        throw new Error('Text content must be a string');
      }
  
      // Legacy Word (.doc) - basic handling
      if (fileType === 'doc' || fileName.endsWith('.doc')) {
        if (typeof file.fileContent === 'string') {
          return file.fileContent.trim(); // Fallback for pre-extracted text
        }
        // Note: Real .doc parsing requires a library or server-side processing
        throw new Error('DOC file parsing not fully supported without additional libraries');
      }
  
      // Unsupported file type
      console.warn(`Unsupported file type: ${fileType} (${fileName})`);
      return null;
    } catch (error) {
      console.error('Error parsing file content:', error);
      setFileError(`Failed to parse ${file.fileName}: ${error.message}`);
      return null;
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    setFileError('');
    
    try {
      const processedFiles = await Promise.all(
        files.map(async (file) => {
          try {
            const processed = await processFile(file);
            // Adapt the processed file to match the expected structure
            return {
              fileName: processed.fileName,
              fileType: processed.fileType,
              fileContent: processed.content // Map 'content' to 'fileContent' for compatibility
            };
          } catch (error) {
            console.error('Error processing file:', error);
            setFileError(error.message);
            return null;
          }
        })
      );
      
      // Filter out any files that failed to process
      const validFiles = processedFiles.filter(file => file !== null);
      
      // Update attached files
      setAttachedFiles(prev => [...prev, ...validFiles]);
      
      // Parse file content for CSV/JSON files
      // For simplicity, we'll just use the last valid file if multiple are uploaded
      if (validFiles.length > 0) {
        const lastFile = validFiles[validFiles.length - 1];
        const parsedData = parseFileContent(lastFile);
        setParsedFileData(parsedData);
      }
      
      // Reset file input
      e.target.value = '';
    } catch (error) {
      console.error('Error handling files:', error);
      setFileError('An error occurred while processing the files.');
    }
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index);
      // If we removed all files or the last file, clear parsed data
      if (newFiles.length === 0) {
        setParsedFileData(null);
      }
      return newFiles;
    });
  };

  const handleKeepFilesToggle = () => {
    setKeepFiles(prev => !prev);
  };

  const handleClearAllFiles = () => {
    setAttachedFiles([]);
    setParsedFileData(null);
    setFileError('');
  };

  return {
    attachedFiles,
    setAttachedFiles,
    keepFiles,
    setKeepFiles,
    fileError,
    setFileError,
    fileInputRef,
    parsedFileData,
    handleAttachmentClick,
    handleFileChange,
    handleRemoveFile,
    handleKeepFilesToggle,
    handleClearAllFiles
  };
};